import { NgIf } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import { ImageProcess, prepareImage, underSizedMessage } from '../image-processing';

export type ImageUploadShape = 'square' | 'circle';
export type ImageUploadSize = 'sm' | 'md' | 'lg';

export const AVATAR_SIZES: Record<ImageUploadSize, string> = { sm: '40', md: '56', lg: '80' };

/** One image: preview, pick, remove. The host saves; this hands over the validated file or processed PNG. */
@Component({
    selector: 'app-image-upload',
    templateUrl: './image-upload.component.html',
    standalone: true,
    imports: [NgIf],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: [':host { display: block; }'],
})
export class ImageUploadComponent implements OnChanges {
    @Input() imageUrl: string | null = null;
    @Input() accept = 'image/png,image/jpeg';
    @Input() maxBytes: number | null = null;
    @Input() hint: string | null = null;
    @Input() shape: ImageUploadShape = 'square';
    @Input() size: ImageUploadSize = 'md';
    @Input() placeholder: 'user' | 'company' | null = null;
    @Input() fallbackSrc: string | null = null;
    @Input() removable = true;
    @Input() process: ImageProcess = 'none';
    @Input() targetWidth: number | null = null;
    @Input() targetHeight: number | null = null;
    @Input() warnIfSmaller = false;
    @Input() disabled = false;
    @Input() saving = false;
    @Input() error: string | null = null;
    @Input() buttonLabel = 'Upload';
    @Input() changeLabel = 'Change';
    @Input() removeLabel = 'Remove';
    @Input() alt = 'Image';
    @Input() dropZone = false;
    @Input() avatar = false;
    @Input() showPending = true;

    @Output() fileChange = new EventEmitter<File>();
    @Output() dataUrlChange = new EventEmitter<string>();
    @Output() removed = new EventEmitter<void>();

    pendingSrc: string | null = null;
    validationError: string | null = null;
    underSized = false;

    constructor(private _cdr: ChangeDetectorRef) {}

    get src(): string | null {
        return this.pendingSrc || this.imageUrl || null;
    }

    get shownError(): string | null {
        return this.validationError || this.error;
    }

    get avatarSize(): string {
        return AVATAR_SIZES[this.size] || AVATAR_SIZES.md;
    }

    get underSizedText(): string {
        return underSizedMessage(this.targetWidth, this.targetHeight);
    }

    get busy(): boolean {
        return this.disabled || this.saving;
    }

    get showDropZone(): boolean {
        return this.dropZone && !this.src && !this.fallbackSrc && !this.placeholder;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['imageUrl'] && !changes['imageUrl'].firstChange) {
            this.pendingSrc = null;
        }
        if (changes['error'] && this.error) {
            this.pendingSrc = null;
        }
    }

    onFiles(event: Event): void {
        this.pick((event as CustomEvent<File[]>).detail?.[0]);
    }

    onPicked(input: HTMLInputElement): void {
        const file = input.files?.[0];
        input.value = '';
        this.pick(file);
    }

    remove(): void {
        if (this.busy) {
            return;
        }
        this.pendingSrc = null;
        this.validationError = null;
        this.underSized = false;
        this.removed.emit();
    }

    private async pick(file: File | undefined): Promise<void> {
        if (!file || this.busy) {
            return;
        }
        try {
            const prepared = await prepareImage(file, {
                accept: this.accept,
                maxBytes: this.maxBytes,
                process: this.process,
                targetWidth: this.targetWidth,
                targetHeight: this.targetHeight,
                warnIfSmaller: this.warnIfSmaller,
            });
            this.validationError = null;
            this.underSized = prepared.underSized;
            this.pendingSrc = this.showPending ? prepared.previewUrl : null;
            if (this.process === 'none') {
                this.fileChange.emit(prepared.file);
            } else {
                this.dataUrlChange.emit(prepared.dataUrl);
            }
        } catch (e) {
            this.validationError = (e as Error).message;
            this.underSized = false;
        }
        this._cdr.markForCheck();
    }
}
